import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { CredentialData, CredentialIsolation, CredentialStatus, SharingMode, SortOption } from '../lib/types';
import type { CredentialResponse } from '../lib/api-client';

export interface CredentialFilters {
  statuses: CredentialStatus[];
  isolation: CredentialIsolation | null;
  search: string;
  sortBy: SortOption;
}

function mapResponse(r: CredentialResponse): CredentialData {
  return {
    object: 'credential',
    id: r.id,
    plan_id: r.plan_id,
    tenant_id: r.tenant_id,
    vault_alias: r.vault_alias,
    service_name: r.service_name,
    access_level: r.access_level,
    access_mode: r.access_mode,
    sharing_mode: r.sharing_mode,
    isolation: r.isolation,
    status: r.status,
    name: r.name,
    credential_type: r.credential_type,
    created_by: r.created_by,
    granted_by: r.granted_by,
    revoked_by: r.revoked_by,
    revocation_reason: r.revocation_reason,
    expires_at: r.expires_at,
    granted_at: r.granted_at,
    revoked_at: r.revoked_at,
    metadata: r.metadata,
    created_at: r.created_at,
    is_enabled_for_tenant: r.is_enabled_for_tenant,
  };
}

export function useCredentials({ enabled = false, pollInterval = 30000 }: { enabled?: boolean; pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [credentials, setCredentials] = useState<CredentialData[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [filters, setFiltersState] = useState<CredentialFilters>({ statuses: [], isolation: null, search: '', sortBy: 'newest' });
  const filtersRef = useRef(filters);

  const hasLoadedOnce = useRef(false);
  const fetchInFlightRef = useRef(false);

  const doFetch = useCallback(() => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    const f = filtersRef.current;
    const query: Record<string, string> = {};
    if (f.statuses.length === 1) query.status = f.statuses[0];
    if (f.isolation) query.isolation = f.isolation;
    apiClient
      .listCredentials(Object.keys(query).length > 0 ? query : undefined)
      .then((res) => {
        setCredentials(res.data.map(mapResponse));
        hasLoadedOnce.current = true;
        setStatus('success');
        setIsRefreshing(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        if (!hasLoadedOnce.current) setStatus('error');
        setIsRefreshing(false);
      })
      .finally(() => { fetchInFlightRef.current = false; });
  }, [apiClient]);

  const doFetchRef = useRef(doFetch);
  doFetchRef.current = doFetch;

  // Initial fetch
  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    doFetchRef.current();
  }, [enabled]);

  // Polling
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      if (fetchInFlightRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setIsRefreshing(true);
      doFetchRef.current();
    }, pollInterval);
    return () => clearInterval(interval);
  }, [enabled, pollInterval]);

  const setFilters = useCallback((f: CredentialFilters) => {
    filtersRef.current = f;
    setFiltersState(f);
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
      fetchInFlightRef.current = false;
      doFetchRef.current();
    }
  }, []);

  const reload = useCallback(() => {
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
      fetchInFlightRef.current = false;
      doFetchRef.current();
    }
  }, []);

  // Client-side filtering (search + multi-status + sort)
  const filtered = useMemo(() => {
    let result = [...credentials];
    const f = filters;

    if (f.statuses.length > 0) {
      result = result.filter((c) => f.statuses.includes(c.status));
    }

    if (f.isolation) {
      result = result.filter((c) => c.isolation === f.isolation);
    }

    if (f.search) {
      const q = f.search.toLowerCase();
      result = result.filter(
        (c) =>
          (c.name?.toLowerCase().includes(q)) ||
          c.service_name.toLowerCase().includes(q) ||
          (c.credential_type?.toLowerCase().includes(q)),
      );
    }

    if (f.sortBy === 'oldest') {
      result.sort((a, b) => a.created_at.localeCompare(b.created_at));
    } else if (f.sortBy === 'recently-updated') {
      result.sort((a, b) => b.created_at.localeCompare(a.created_at));
    } else {
      result.sort((a, b) => b.created_at.localeCompare(a.created_at));
    }

    return result;
  }, [credentials, filters]);

  const counts = useMemo(() => {
    const c: Record<CredentialStatus, number> = { pending: 0, active: 0, expired: 0, revoked: 0 };
    for (const cred of credentials) { c[cred.status] = (c[cred.status] ?? 0) + 1; }
    return c;
  }, [credentials]);

  const isolationCounts = useMemo(() => {
    const c: Record<CredentialIsolation, number> = { tenant: 0, personal: 0, system: 0 };
    for (const cred of credentials) { c[cred.isolation] = (c[cred.isolation] ?? 0) + 1; }
    return c;
  }, [credentials]);

  // CRUD operations
  const createCredential = useCallback(async (body: {
    name: string;
    credential_type: string;
    service_name: string;
    access_level: 'read' | 'read_write';
    sharing_mode?: SharingMode;
    isolation?: CredentialIsolation;
    fields?: Record<string, unknown>;
  }) => {
    const created = await apiClient.createCredential(body);
    const mapped = mapResponse(created);
    setCredentials((prev) => [mapped, ...prev]);
    return mapped;
  }, [apiClient]);

  const updateCredential = useCallback(async (id: string, body: {
    name?: string;
    sharing_mode?: SharingMode;
    isolation?: CredentialIsolation;
    fields?: Record<string, unknown>;
  }) => {
    const updated = await apiClient.updateCredential(id, body);
    const mapped = mapResponse(updated);
    setCredentials((prev) => prev.map((c) => c.id === id ? mapped : c));
    return mapped;
  }, [apiClient]);

  const deleteCredential = useCallback(async (id: string) => {
    setCredentials((prev) => prev.filter((c) => c.id !== id));
    try {
      await apiClient.deleteCredential(id);
    } catch (err) {
      // Rollback — re-fetch
      doFetchRef.current();
      throw err;
    }
  }, [apiClient]);

  const revokeCredential = useCallback(async (id: string, reason?: string) => {
    const revoked = await apiClient.revokeCredential(id, reason);
    const mapped = mapResponse(revoked);
    setCredentials((prev) => prev.map((c) => c.id === id ? mapped : c));
    return mapped;
  }, [apiClient]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectCredential = useCallback((id: string | null) => setSelectedId(id), []);

  return {
    credentials: filtered,
    allCredentials: credentials,
    status,
    isRefreshing,
    error,
    counts,
    isolationCounts,
    selectedId,
    selectCredential,
    reload,
    filters,
    setFilters,
    createCredential,
    updateCredential,
    deleteCredential,
    revokeCredential,
  };
}
